import {useEffect, useRef} from 'react';
import {
  NewCustomerMessageEvent,
  NewInvoiceEvent,
  NewInvoiceMessageEvent,
  NewQuoteEvent,
  NewQuoteMessageEvent,
  useSocketEvents,
} from 'appsockets/SocketEventProvider';
import {INotification} from 'constants/interfaces';
import {reduxAddNotification} from 'appredux/actions';
import NotificationSound from 'assets/sounds/notification.mp3';
import {useLocation} from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/naming-convention
export function useNotificationListener() {
  const {
    newCustomerMessage,
    newQuote,
    newQuoteMessage,
    newInvoice,
    newInvoiceMessage,
  } = useSocketEvents();

  const location = useLocation();
  const audioObj = useRef(new Audio(NotificationSound));
  const playNotificationSound = () => audioObj?.current?.play();

  useEffect(() => {
    if (!newCustomerMessage) return;
    const isRead =
      location.pathname === '/customers/' + newCustomerMessage.sender.id;
    handleNewNotification(
      newCustomerMessageNotification(newCustomerMessage, isRead),
    );
  }, [newCustomerMessage]);

  useEffect(() => {
    if (!newQuote) return;
    handleNewNotification(newQuoteNotification(newQuote));
  }, [newQuote]);

  useEffect(() => {
    if (!newQuoteMessage) return;
    const isRead = location.pathname === '/quotes/' + newQuoteMessage.quote.id;
    handleNewNotification(newQuoteMessageNotification(newQuoteMessage, isRead));
  }, [newQuoteMessage]);

  useEffect(() => {
    if (!newInvoice) return;
    handleNewNotification(newInvoiceNotification(newInvoice));
  }, [newInvoice]);

  useEffect(() => {
    if (!newInvoiceMessage) return;
    const isRead =
      location.pathname === '/orders/' + newInvoiceMessage.invoice.id;
    handleNewNotification(
      newInvoiceMessageNotification(newInvoiceMessage, isRead),
    );
  }, [newInvoiceMessage]);

  const handleNewNotification = (notification: INotification) => {
    playNotificationSound();
    reduxAddNotification(notification);
  };
}

const newCustomerMessageNotification = (
  newCustomerMessage: NewCustomerMessageEvent,
  isRead: boolean,
): INotification => ({
  id: `customerMessage#${newCustomerMessage.message.id}`,
  date: new Date(),
  heading: `New message from ${newCustomerMessage.sender.name}`,
  body: `"${newCustomerMessage.message.message || ''}"`,
  link: `/customers/${newCustomerMessage.sender.id}`,
  isRead,
});

const newQuoteNotification = (newQuote: NewQuoteEvent): INotification => ({
  id: `quote#${newQuote.quote.id}`,
  date: new Date(),
  heading: `New Quote #${newQuote.quote.id}`,
  body: `Total R${newQuote.quote.total.toFixed(2)}`,
  link: `/quotes/${newQuote.quote.id}`,
});

const newQuoteMessageNotification = (
  newQuoteMessage: NewQuoteMessageEvent,
  isRead: boolean,
): INotification => ({
  id: `quoteMessage#${newQuoteMessage.message.id}`,
  date: new Date(),
  heading: `[Quote #${newQuoteMessage.quote.id}] New message from ${newQuoteMessage.sender.name}`,
  body: `"${newQuoteMessage.message.textMessage || ''}"`,
  link: `/quotes/${newQuoteMessage.quote.id}`,
  isRead,
});

const newInvoiceNotification = (
  newInvoice: NewInvoiceEvent,
): INotification => ({
  id: `order#${newInvoice.invoice.id}`,
  date: new Date(),
  heading: `New Order #${newInvoice.invoice.id}`,
  body: `Total R${newInvoice.invoice.total.toFixed(2)}`,
  link: `/orders/${newInvoice.invoice.id}`,
});

const newInvoiceMessageNotification = (
  newInvoiceMessage: NewInvoiceMessageEvent,
  isRead: boolean,
): INotification => ({
  id: `orderMessage#${newInvoiceMessage.message.id}`,
  date: new Date(),
  heading: `[Invoice #${newInvoiceMessage.invoice.id}] New message from ${newInvoiceMessage.sender.name} `,
  body: `"${newInvoiceMessage.message.textMessage || ''}"`,
  link: `/orders/${newInvoiceMessage.invoice.id}`,
  isRead,
});
